import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Card } from '../components/ui/card';
import ProgressBar from '../components/ProgressBar';
import PageHeader from '../components/PageHeader';

const sauces = [
  { id: 'korean-sauce', name: 'Korean Sauce', price: 0.50 },
  { id: 'garlic-mayo', name: 'Garlic Mayo', price: 0.50 },
  { id: 'smokey-bbq', name: 'Smokey BBQ', price: 0.50 },
  { id: 'no-sauce', name: 'Geen saus', price: 0 }
];

const BurgerChoiceCard = ({ selected, onClick, choiceId, image, imageAlt, imageScale, title, subtitle, price }) => (
  <Card
    onClick={onClick}
    data-testid={`burger-choice-${choiceId}`}
    className={`product-card p-10 cursor-pointer border-2 bg-card hover:border-primary touch-feedback min-h-[200px] flex flex-col items-center justify-center ${
      selected ? 'border-primary bg-primary/5' : 'border-border'
    }`}
  >
    <div className="w-28 h-28 rounded-full overflow-hidden mb-4">
      <img src={image} alt={imageAlt} className={`w-full h-full object-cover ${imageScale}`} />
    </div>
    <h3 className="text-2xl font-bold text-foreground mb-2">{title}</h3>
    <p className="text-muted-foreground text-center mb-3">{subtitle}</p>
    {price && <span className="text-xl font-bold text-primary">{price}</span>}
  </Card>
);

const SauceSelection = ({ selectedSauce, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="mb-8"
  >
    <h3 className="text-2xl font-semibold text-foreground mb-4">Kies je saus</h3>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {sauces.map((sauce) => (
        <Card
          key={sauce.id}
          onClick={() => onSelect(sauce)}
          data-testid={`sauce-${sauce.id}`}
          className={`product-card p-6 cursor-pointer border-2 bg-card hover:border-primary touch-feedback text-center ${
            selectedSauce?.id === sauce.id ? 'border-primary bg-primary/5' : 'border-border'
          }`}
        >
          <h4 className="text-lg font-semibold text-foreground mb-2">{sauce.name}</h4>
          {sauce.price > 0 && (
            <p className="text-sm font-bold text-primary">+{'\u20AC'}{sauce.price.toFixed(2)}</p>
          )}
          {selectedSauce?.id === sauce.id && (
            <div className="mt-2 flex justify-center">
              <Check className="w-5 h-5 text-primary" />
            </div>
          )}
        </Card>
      ))}
    </div>
  </motion.div>
);

export const BurgerOption = ({ order, setOrder }) => {
  const navigate = useNavigate();
  const [makeItBurger, setMakeItBurger] = React.useState(order.burger || false);
  const [selectedSauce, setSelectedSauce] = React.useState(order.burgerSauce);

  const isEligibleForBurger = order.product?.category === 'burger';

  const handleContinue = () => {
    setOrder({ 
      ...order, 
      burger: makeItBurger, 
      burgerSauce: makeItBurger ? selectedSauce : null 
    });
    navigate('/side');
  };

  React.useEffect(() => {
    if (!isEligibleForBurger) {
      setOrder(prev => ({ ...prev, burger: false, burgerSauce: null }));
      navigate('/side');
    }
  }, [isEligibleForBurger, navigate, setOrder]);

  if (!isEligibleForBurger) return null;

  return (
    <div className="min-h-screen bg-background pb-8">
      <ProgressBar currentStep={4} totalSteps={5} />
      <PageHeader title="Make it a Burger?" subtitle="Wil je er een burger van maken?" />

      <div className="container max-w-5xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
            <BurgerChoiceCard
              selected={makeItBurger}
              choiceId="yes"
              onClick={() => { setMakeItBurger(true); if (!selectedSauce) setSelectedSauce(sauces[0]); }}
              image="https://customer-assets.emergentagent.com/job_chicken-order-flow/artifacts/bow7ntek_1.png"
              imageAlt="Chicken Burger"
              imageScale="scale-125"
              title="Ja, maak er een burger van!"
              subtitle="Met broodje en saus"
              price={`+\u20AC1.50`}
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
            <BurgerChoiceCard
              selected={!makeItBurger}
              choiceId="no"
              onClick={() => { setMakeItBurger(false); setSelectedSauce(null); }}
              image="https://customer-assets.emergentagent.com/job_chicken-order-flow/artifacts/9tqfs5la_3.png"
              imageAlt="Fried Chicken"
              imageScale="scale-[1.6]"
              title="Nee, alleen de kip"
              subtitle="Zonder broodje"
            />
          </motion.div>
        </div>

        {makeItBurger && <SauceSelection selectedSauce={selectedSauce} onSelect={setSelectedSauce} />}

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex justify-center">
          <button
            onClick={handleContinue}
            disabled={makeItBurger && !selectedSauce}
            data-testid="burger-continue-btn"
            className="btn-kiosk bg-primary hover:bg-primary/90 text-primary-foreground px-16 py-8 text-xl font-semibold shadow-lg rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            Ga verder
            <ArrowRight className="w-6 h-6" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default BurgerOption;
